// Utils
import styled, { css } from 'styled-components'

// Hooks
import { useFormContext } from 'react-hook-form'
import { useBoolean } from 'usehooks-ts'

// Icons
import { AiFillEye } from 'react-icons/ai'

export interface IProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
  label: string
  rounded?: boolean
  validation?: object
  err?: string | undefined
}

interface IInputContainerProps {
  hasError: boolean
  rounded?: boolean
}

const Input: React.FC<IProps> = ({
  name,
  label,
  rounded,
  type,
  validation,
  required,
  err,
  ...rest
}) => {
  const { register, watch } = useFormContext()

  const { value: showPassword, toggle } = useBoolean(false)

  const value = watch(name)

  return (
    <InputContainer className="input">
      <InputGroup rounded={rounded} hasError={!!err}>
        <input
          {...rest}
          id={name}
          className="form-input"
          aria-invalid="true"
          type={showPassword ? 'text' : type}
          required={required}
          {...register(name, validation)}
        />
        <label
          htmlFor={name}
          className={`form-input-label  ${
            value?.toString().length ? 'shrink' : ''
          }`}
        >
          {label}
        </label>
        {(type === 'password' || showPassword) && (
          <figure
            onClick={(e) => {
              e.preventDefault()
              toggle()
            }}
          >
            <AiFillEye />
          </figure>
        )}
      </InputGroup>
      <p>{err}</p>
    </InputContainer>
  )
}

export default Input

const InputContainer = styled.div`
  p {
    margin-left: 1.5rem;
  }
`

const shrinkLabel = css`
  left: 3rem;
  top: 0.4rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.fonts.primary};
`
const InputGroup = styled.div<IInputContainerProps>`
  position: relative;
  .form-input {
    background-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.fonts.primary};
    font-size: 1.8rem;
    padding: 2rem 0.5rem 1.5rem 3rem;
    display: block;
    width: 100%;
    border: ${({ theme, hasError }) =>
      hasError ? `red` : `${theme.colors.secondary}`};
    border-radius: ${({ rounded }) => (rounded ? '999rem' : '.1rem')};
    margin: 2rem 0 1rem;
    font-family: inherit;
    &:focus {
      outline: none;
    }
    &:focus ~ .form-input-label {
      ${shrinkLabel}
    }
  }
  input[type='password'] {
    letter-spacing: 0.3em;
  }
  .form-input-label {
    color: ${({ theme }) => theme.fonts.primary};
    font-size: 1.8rem;
    font-weight: normal;
    position: absolute;
    pointer-events: none;
    left: 3rem;
    top: 1.5rem;
    transition: 300ms ease all;

    &.shrink {
      ${shrinkLabel}
    }
  }

  figure {
    cursor: pointer;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 2.5rem;
  }
`
